import clsx from 'clsx';
import {CSSProperties, ReactNode} from 'react';
import {IconSize, SvgIcon} from '@ui/icons/svg-icon';

interface Props {
  type?: string;
  mime?: string | null;
  extension?: string | null;
  className?: string;
  size?: IconSize;
  color?: string;
}

type FileIconKind =
  | 'default'
  | 'audio'
  | 'video'
  | 'text'
  | 'pdf'
  | 'archive'
  | 'folder'
  | 'sharedFolder'
  | 'image'
  | 'powerPoint'
  | 'word'
  | 'spreadsheet';

interface FileIconMeta {
  label: string;
  className: string;
  symbol: FileIconKind;
}

const FileIconMeta: Record<FileIconKind, FileIconMeta> = {
  default: {
    label: 'FILE',
    className: 'default-file-color',
    symbol: 'default',
  },
  audio: {
    label: 'AUDIO',
    className: 'audio-file-color',
    symbol: 'audio',
  },
  video: {
    label: 'VIDEO',
    className: 'video-file-color',
    symbol: 'video',
  },
  text: {
    label: 'TXT',
    className: 'text-file-color',
    symbol: 'text',
  },
  pdf: {
    label: 'PDF',
    className: 'pdf-file-color',
    symbol: 'pdf',
  },
  archive: {
    label: 'ZIP',
    className: 'archive-file-color',
    symbol: 'archive',
  },
  folder: {
    label: 'FOLDER',
    className: 'folder-file-color',
    symbol: 'folder',
  },
  sharedFolder: {
    label: 'SHARE',
    className: 'shared-folder-file-color',
    symbol: 'sharedFolder',
  },
  image: {
    label: 'IMG',
    className: 'image-file-color',
    symbol: 'image',
  },
  powerPoint: {
    label: 'PPT',
    className: 'power-point-file-color',
    symbol: 'powerPoint',
  },
  word: {
    label: 'DOC',
    className: 'word-file-color',
    symbol: 'word',
  },
  spreadsheet: {
    label: 'XLS',
    className: 'spreadsheet-file-color',
    symbol: 'spreadsheet',
  },
};

export function FileTypeIcon({
  type,
  mime,
  extension,
  className,
  size,
  color,
}: Props) {
  const iconType = normalizeType(type, mime, extension);
  const meta = FileIconMeta[iconType] || FileIconMeta.default;
  const label = getLabel(extension, meta.label, iconType);

  return (
    <SvgIcon
      size={size}
      className={clsx('file-type-icon', className, color ?? meta.className)}
      viewBox="0 0 64 64"
      fill="fill-none"
      style={
        {
          '--file-icon-label-length': label.length,
        } as CSSProperties
      }
    >
      {iconType === 'folder' || iconType === 'sharedFolder' ? (
        <FolderBadge label={label} shared={iconType === 'sharedFolder'} />
      ) : (
        <DocumentBadge label={label} symbol={meta.symbol} />
      )}
    </SvgIcon>
  );
}

function normalizeType(
  type?: string,
  mime?: string | null,
  extension?: string | null,
): FileIconKind {
  if (type === 'folder' || type === 'sharedFolder') {
    return type;
  }

  const typeFromExtension = getTypeFromExtension(extension);
  if (typeFromExtension) {
    return typeFromExtension;
  }

  const typeFromMime = getTypeFromMime(mime);
  if (typeFromMime) {
    return typeFromMime;
  }

  if (!type && mime) {
    type = mime.split('/')[0];
  }

  switch (type) {
    case 'audio':
    case 'video':
    case 'text':
    case 'pdf':
    case 'archive':
    case 'folder':
    case 'sharedFolder':
    case 'image':
    case 'powerPoint':
    case 'word':
    case 'spreadsheet':
      return type;
    case 'image/svg+xml':
    case 'photoshop':
      return 'image';
    case 'text/plain':
      return 'text';
    case 'postscript':
      return 'default';
    default:
      return 'default';
  }
}

function getTypeFromExtension(
  extension: string | null | undefined,
): FileIconKind | null {
  const ext = extension?.replace(/^\./, '').trim().toLowerCase();
  if (!ext) {
    return null;
  }

  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'bmp', 'svg'].includes(ext)) {
    return 'image';
  }

  if (['csv', 'xls', 'xlsx', 'ods', 'numbers'].includes(ext)) {
    return 'spreadsheet';
  }

  if (['doc', 'docx', 'odt', 'pages', 'rtf'].includes(ext)) {
    return 'word';
  }

  if (['ppt', 'pptx', 'odp', 'key'].includes(ext)) {
    return 'powerPoint';
  }

  if (['pdf'].includes(ext)) {
    return 'pdf';
  }

  if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz'].includes(ext)) {
    return 'archive';
  }

  if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'].includes(ext)) {
    return 'audio';
  }

  if (['mp4', 'mov', 'webm', 'avi', 'mkv', 'm4v'].includes(ext)) {
    return 'video';
  }

  if (['txt', 'md', 'json', 'xml', 'yml', 'yaml', 'log'].includes(ext)) {
    return 'text';
  }

  return null;
}

function getTypeFromMime(mime: string | null | undefined): FileIconKind | null {
  const normalizedMime = mime?.trim().toLowerCase();
  if (!normalizedMime) {
    return null;
  }

  if (normalizedMime === 'application/pdf') {
    return 'pdf';
  }

  if (
    [
      'text/csv',
      'application/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.oasis.opendocument.spreadsheet',
    ].includes(normalizedMime)
  ) {
    return 'spreadsheet';
  }

  if (
    [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.oasis.opendocument.text',
      'application/rtf',
    ].includes(normalizedMime)
  ) {
    return 'word';
  }

  if (
    [
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.oasis.opendocument.presentation',
    ].includes(normalizedMime)
  ) {
    return 'powerPoint';
  }

  if (
    [
      'application/zip',
      'application/x-zip-compressed',
      'application/x-rar-compressed',
      'application/x-7z-compressed',
      'application/gzip',
    ].includes(normalizedMime)
  ) {
    return 'archive';
  }

  return null;
}

function getLabel(
  extension: string | null | undefined,
  fallback: string,
  type: FileIconKind,
): string {
  if (type === 'folder' || type === 'sharedFolder') {
    return fallback;
  }

  const label = extension?.replace(/^\./, '').trim().toUpperCase();
  if (label && label.length <= 5) {
    return label;
  }

  return fallback;
}

interface DocumentBadgeProps {
  label: string;
  symbol: FileIconKind;
}
function DocumentBadge({label, symbol}: DocumentBadgeProps) {
  return (
    <g>
      <path
        d="M18 4.5h24l12 12V56a3.5 3.5 0 0 1-3.5 3.5h-33A3.5 3.5 0 0 1 14 56V8a3.5 3.5 0 0 1 4-3.5Z"
        fill="var(--file-icon-bg)"
        stroke="var(--file-icon-accent)"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path
        d="M42 5v10.5c0 .8.7 1.5 1.5 1.5H54"
        fill="var(--file-icon-corner)"
        stroke="var(--file-icon-accent)"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <g className="file-type-icon-symbol">{renderSymbol(symbol)}</g>
      <LabelPill label={label} />
    </g>
  );
}

interface FolderBadgeProps {
  label: string;
  shared?: boolean;
}
function FolderBadge({label, shared}: FolderBadgeProps) {
  return (
    <g>
      <path
        d="M6.5 17.5A4.5 4.5 0 0 1 11 13h13.2c1.4 0 2.8.6 3.7 1.7l3 3.3H53a4.5 4.5 0 0 1 4.5 4.5V50A4.5 4.5 0 0 1 53 54.5H11A4.5 4.5 0 0 1 6.5 50Z"
        fill="var(--file-icon-bg)"
        stroke="var(--file-icon-accent)"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path
        d="M6.5 24.5h51V50A4.5 4.5 0 0 1 53 54.5H11A4.5 4.5 0 0 1 6.5 50Z"
        fill="var(--file-icon-soft)"
        stroke="var(--file-icon-accent)"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      {shared ? <SharedSymbol /> : <FolderLines />}
      <LabelPill label={label} />
    </g>
  );
}

function LabelPill({label}: {label: string}) {
  const fontSize = label.length > 4 ? 6.2 : 7.2;
  return (
    <g>
      <rect
        x="15"
        y="46.8"
        width="34"
        height="12.2"
        rx="3.2"
        fill="var(--file-icon-label)"
      />
      <text
        x="32"
        y="55.2"
        textAnchor="middle"
        fill="white"
        fontFamily="Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif"
        fontSize={fontSize}
        fontWeight="800"
        letterSpacing=".25"
      >
        {label}
      </text>
    </g>
  );
}

function renderSymbol(symbol: FileIconKind): ReactNode {
  switch (symbol) {
    case 'audio':
      return <AudioSymbol />;
    case 'video':
      return <VideoSymbol />;
    case 'text':
      return <TextSymbol />;
    case 'pdf':
      return <PdfSymbol />;
    case 'archive':
      return <ArchiveSymbol />;
    case 'image':
      return <ImageSymbol />;
    case 'powerPoint':
      return <PowerPointSymbol />;
    case 'word':
      return <WordSymbol />;
    case 'spreadsheet':
      return <SpreadsheetSymbol />;
    default:
      return <DefaultSymbol />;
  }
}

function DefaultSymbol() {
  return (
    <g
      stroke="var(--file-icon-accent)"
      strokeWidth="2.2"
      strokeLinecap="round"
    >
      <path d="M22 25h20" />
      <path d="M22 31h15" />
      <path d="M22 37h20" />
    </g>
  );
}

function TextSymbol() {
  return (
    <g
      stroke="var(--file-icon-accent)"
      strokeWidth="2.2"
      strokeLinecap="round"
    >
      <path d="M22 23h20" />
      <path d="M22 29h20" />
      <path d="M22 35h15" />
      <path d="M22 41h12" />
    </g>
  );
}

function ImageSymbol() {
  return (
    <g
      stroke="var(--file-icon-accent)"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="25" cy="25" r="3.2" fill="var(--file-icon-soft)" />
      <path d="M20.5 40.5 28 33l4.2 4.2L39.5 30 46 40.5Z" />
    </g>
  );
}

function VideoSymbol() {
  return (
    <path
      d="M27 24.8v16.4L42.5 33Z"
      fill="var(--file-icon-soft)"
      stroke="var(--file-icon-accent)"
      strokeWidth="2.2"
      strokeLinejoin="round"
    />
  );
}

function AudioSymbol() {
  return (
    <g
      stroke="var(--file-icon-accent)"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M35 21v17.8" />
      <path d="M35 22.5h9v6h-9" />
      <circle cx="29.5" cy="40" r="4" fill="var(--file-icon-soft)" />
    </g>
  );
}

function PdfSymbol() {
  return <TypeLetter letter="P" />;
}

function WordSymbol() {
  return <TypeLetter letter="W" />;
}

function PowerPointSymbol() {
  return <TypeLetter letter="P" />;
}

function TypeLetter({letter}: {letter: string}) {
  return (
    <text
      x="32"
      y="38"
      textAnchor="middle"
      fill="var(--file-icon-accent)"
      fontFamily="Inter, ui-sans-serif, system-ui, sans-serif"
      fontSize="21"
      fontWeight="800"
    >
      {letter}
    </text>
  );
}

function SpreadsheetSymbol() {
  return (
    <g
      stroke="var(--file-icon-accent)"
      strokeWidth="1.8"
      strokeLinejoin="round"
    >
      <rect
        x="21"
        y="22"
        width="22"
        height="20"
        rx="2"
        fill="var(--file-icon-soft)"
      />
      <path d="M28.5 22v20M35.5 22v20M21 28.7h22M21 35.3h22" />
    </g>
  );
}

function ArchiveSymbol() {
  return (
    <g
      stroke="var(--file-icon-accent)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M32 20v22" />
      <path d="M27 24h5M32 28h5M27 32h5M32 36h5" />
      <rect
        x="27"
        y="40"
        width="10"
        height="4"
        rx="1.5"
        fill="var(--file-icon-soft)"
      />
    </g>
  );
}

function FolderLines() {
  return (
    <g
      stroke="var(--file-icon-accent)"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <path d="M18 32h16" />
      <path d="M18 38h10" />
    </g>
  );
}

function SharedSymbol() {
  return (
    <g
      stroke="var(--file-icon-accent)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="25" cy="33" r="3.5" fill="var(--file-icon-soft)" />
      <circle cx="39" cy="33" r="3.5" fill="var(--file-icon-soft)" />
      <path d="M20 42c1.6-3 4.2-4.5 7.8-4.5" />
      <path d="M36.2 37.5c3.6 0 6.2 1.5 7.8 4.5" />
    </g>
  );
}
